import React, {
  useState, useEffect, useMemo, useCallback,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { EmptyState } from './EmptyState';
import { Screens } from './MultiAgentsHomeHandler.enum';
import { AccountWithoutAds } from './AccountWithoutAds';
import { AccountWithOneDraft } from './AccountWithOneDraft';
import AdsInsights from './AdsInsights';
import AdsInsightsSkeleton from './AdsInsights/components/AdsInsightsSkeleton';
import MyAds from './MyAds';
import { Header, HeaderSkeleton } from './Header';
import { useChatbot } from '@/contexts/Chatbot';
import { useAiChat } from '@/hooks/AiChat';
import useLocale from '@/hooks/useLocale';
import * as Styles from './MultiAgentsHomeHandler.styles';
import { SocialMediaIcons } from 'gatorcomponents';
import { adsHandlerActions } from '@/redux/modules/adsHandler';
import { DisconnectAccountModal } from './DisconnectAccountModal';
import { STEPS } from './DisconnectAccountModal/DisconnectAccountModal.enum';

const MultiAgentsHomeHandler = () => {
  const dispatch = useDispatch();
  const {
    pages, drafts, insights, unlinkAccount,
  } = useSelector(state => state.adsHandler);
  const chatbot = useChatbot();
  const { triggerExternalMessage } = useAiChat();
  const { marketing: marketingLocale } = useLocale();
  const { multiAgentScreens: multiAgentScreensLocale } = marketingLocale;
  const { header: headerLocale } = multiAgentScreensLocale;

  const [currentScreen, setCurrentScreen] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [showRemoveAccountModal, setShowRemoveAccountModal] = useState(false);
  const [unlinkModalStep, setUnlinkModalStep] = useState(STEPS.ASKING_FOR_CONFIRMATION);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    dispatch(adsHandlerActions.pages.request());
    dispatch(adsHandlerActions.drafts.request());
    dispatch(adsHandlerActions.insights.request({}));
  }, [dispatch]);

  useEffect(() => {
    const hasAllDataLoaded = pages.status !== null && pages.status !== 'loading'
      && drafts.status !== null && drafts.status !== 'loading'
      && insights.status !== null && insights.status !== 'loading';

    if (isInitialLoad && hasAllDataLoaded) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, pages.status, drafts.status, insights.status]);

  useEffect(() => {
    if (pages.status === 'loading') {
      setCurrentScreen(null);
      return;
    }

    if (pages.status === 'failure') {
      setCurrentScreen(Screens.EMPTY_STATE);
      return;
    }

    if (isInitialLoad && (pages.status === 'loading' || drafts.status === 'loading' || insights.status === 'loading')) {
      setCurrentScreen(null);
      return;
    }

    if (drafts.status !== 'success' || insights.status !== 'success') {
      return;
    }

    const draftsCount = (drafts.data && drafts.data.drafts) ? drafts.data.drafts.length : 0;
    const insightsRecents = (insights.data && insights.data.recents) ? insights.data.recents : [];
    const hasNonDraftCampaigns = insightsRecents.some(item => item.status !== 'draft');

    if (draftsCount === 1 && !hasNonDraftCampaigns) {
      setCurrentScreen(Screens.WITH_ONE_DRAFT);
      return;
    }

    if ((hasNonDraftCampaigns || draftsCount > 1) && pages.list.length > 0) {
      setCurrentScreen(Screens.ADS_INSIGHTS);
      return;
    }

    if (pages.status === 'success' && !isInitialLoad) {
      const hasPages = pages.list && pages.list.length > 0;
      setCurrentScreen(hasPages ? Screens.EMPTY_ADS : Screens.EMPTY_STATE);
    }
  }, [isInitialLoad, pages.status, pages.list, drafts.status, drafts.data, insights.status, insights.data]);

  const handleSuccessUnlinkAccount = useCallback(() => {
    if (unlinkAccount.unlinked) {
      setCurrentScreen(Screens.EMPTY_STATE);
      setUnlinkModalStep(STEPS.SUCCESS);
    }
  }, [unlinkAccount.unlinked]);

  useEffect(() => {
    handleSuccessUnlinkAccount();
  }, [handleSuccessUnlinkAccount]);

  const currentPage = pages.list[selectedPageIndex];
  const platform = currentPage && currentPage.platform;
  const account = currentPage && currentPage.account;

  const SelectedAccountLabel = useMemo(() => {
    if (!platform || !account) {
      return null;
    }

    return (
      <Styles.SelectedAccountRow>
        {platform && (
          <Styles.SocialMediaWrapper $platform={platform}>
            <SocialMediaIcons icon={platform} color="#fff" size="sm" />
          </Styles.SocialMediaWrapper>
        )}
        <Styles.AccountName>
          {account}
        </Styles.AccountName>
      </Styles.SelectedAccountRow>
    );
  }, [platform, account]);

  const options = useMemo(() => {
    if (!SelectedAccountLabel) return [];
    return [{
      label: SelectedAccountLabel,
      value: SelectedAccountLabel,
    }];
  }, [SelectedAccountLabel]);


  const handleOpenGator = ({
    message,
  }) => {
    if (chatbot.isActive) {
      chatbot.setRoute('general');
    }
    triggerExternalMessage({ message, websocketType: 'multi-agent' });
  };

  const getAccountWithoutAdsProps = () => ({
    handleOpenGator,
  });

  const getAccountWithOneDraftProps = () => {
    const draft = (drafts.data && drafts.data.drafts && drafts.data.drafts.length > 0)
      ? drafts.data.drafts[0]
      : null;
    return {
      handleOpenGator,
      options,
      SelectedAccountLabel,
      draft,
    };
  };

  const goToMyAds = () => {
    setCurrentScreen(Screens.MY_ADS);
  };

  const goToInsights = () => {
    setCurrentScreen(Screens.ADS_INSIGHTS);
  };

  const getAdsInsightsProps = () => ({
    goToMyAds,
    dateFrom,
    dateTo,
  });

  const screens = {
    [Screens.EMPTY_STATE]: <EmptyState />,
    [Screens.EMPTY_ADS]: <AccountWithoutAds {...getAccountWithoutAdsProps()} />,
    [Screens.WITH_ONE_DRAFT]: <AccountWithOneDraft {...getAccountWithOneDraftProps()} />,
    [Screens.ADS_INSIGHTS]: <AdsInsights {...getAdsInsightsProps()} />,
    [Screens.MY_ADS]: <MyAds goToInsights={goToInsights} />,
  };

  const hasConnectedAccount = useMemo(() => pages.list && pages.list.length > 0, [pages.list]);

  const handleAccountChange = (selectedAccount) => {
    const index = pages.list.findIndex(page => page.id === selectedAccount.id);
    if (index !== -1) {
      setSelectedPageIndex(index);
    }
  };

  const handleRemoveAccount = () => {
    setShowRemoveAccountModal(true);
  };

  const getHeaderProps = () => ({
    selectedAccount: {
      name: account,
      type: platform,
    },
    connectedAccounts: pages.list.map(page => ({
      id: page.id,
      name: page.account,
      type: page.platform,
      description: headerLocale.accountPlatformDescription(page.platform),
    })),
    dateFrom,
    dateTo,
    onDateFromChange: setDateFrom,
    onDateToChange: setDateTo,
    onAccountChange: handleAccountChange,
    handleRemoveAccount,
  });

  const getDisconnectAccountModalProps = () => {
    const onClose = () => {
      setShowRemoveAccountModal(false);
    };

    const handleDisconnect = () => {
      dispatch(adsHandlerActions.unlinkAccount.unlinkAccountRequest());
    };


    return ({
      onClose,
      handleDisconnect,
      unlinkModalStep,
      setUnlinkModalStep,
      stepLocale: multiAgentScreensLocale.disconnectModalStepLocale,
    });
  };

  const isLoadingInitialData = isInitialLoad && (
    pages.status === null || pages.status === 'loading'
    || drafts.status === null || drafts.status === 'loading'
    || insights.status === null || insights.status === 'loading'
  );

  if ((isLoadingInitialData && pages.status !== 'failure') || currentScreen === null) {
    return (
      <Styles.Container>
        <HeaderSkeleton />
        <AdsInsightsSkeleton />
      </Styles.Container>
    );
  }

  return (
    <Styles.Container>
      {showRemoveAccountModal && (
        <DisconnectAccountModal {...getDisconnectAccountModalProps()} />
      )}

      {hasConnectedAccount && (
        <Header {...getHeaderProps()} />
      )}

      {screens[currentScreen]}
    </Styles.Container>
  );
};

export default MultiAgentsHomeHandler;
